import { IOffices } from './IGdsCredentials.model';
import { ExchangeAvailabilityOption } from './voluntary-change.model';

//Interfaces from Seat
export interface SeatMapRequest {
  pnrOption: string;
  selectedOption: ExchangeAvailabilityOption;
  origin: string;
  destination: string;
  marketingCarrier: string;
  marketingFlightNumber: string;
  operatingCarrier: string;
  operatingFlightNumber: string;
  departureDate: string;
  pnr: string;
  gds: string;
  cityOfficeId: string;
  classOfService: string;
  currency: string;
  isAirline: boolean;
  officeCountry?: IOffices;
}

export interface Flight {
  origin: string;
  destination: string;
  departureDate: string;
  operatingCarrier: string;
  operating: string;
  marketingCarrier: string;
  marketing: string;
}

export interface CabinClass {
  rbd: string;
  marketingDescription: string;
}

export interface CabinRowSeatOfferPrice {
  currencyCode: string;
  totalAmount: number;
}

export interface ICabinRowSeatOfferTaxes {
  taxCode: string;
  totalAmount: CabinRowSeatOfferPrice;
}

export interface CabinRowSeatOffer {
  entitledInd: boolean;
  commercialName: string;
  travellerID: string;
  price: CabinRowSeatOfferPrice;
  taxes: ICabinRowSeatOfferTaxes[];
}

export interface CabinRowSeat {
  premiumInd: boolean;
  inoperativeInd: boolean;
  occupiedInd: boolean;
  restrictedReclineInd: boolean;
  chargeableInd: boolean;
  noInfantInd: boolean;
  exitRowInd: boolean;
  number: string;
  occupation: string;
  location: string;
  listOflocation: [];
  bilateral: string;
  occupationDescription: string;
  facility: string;
  facilityDescription: string;
  limitation: string;
  limitationDescription: string;
  offer: CabinRowSeatOffer;
}

export interface CabinRow {
  rowNumber: string;
  seat: CabinRowSeat[];
}

export interface SeatSelected {
  seat: string;
  action: SeatSelectedEnum;
  cancelation: boolean;
}

export interface SeatSelectedByPassenger {
  seatSelected: SeatSelected;
  passengerId: string;
}

export interface SeatSelectedOption {
  seat: string;
  passengerId: string;
  seatPrice: IReservationSeatSeatPrice;
}

export enum SeatSelectedEnum {
  add = 'add',
  remove = 'remove'
}

export interface CabinColumn {
  column: string;
  characteristics: string | null;
}

export interface Cabin {
  seatOccupationDefault: string;
  firstRow: number;
  lastRow: number;
  classLocation: string;
  cabinClass: CabinClass;
  row: CabinRow[];
  column: CabinColumn[];
}

export interface FareAvailQualifiersLoyalty {
  carrier: string;
  tag: string;
  description: string;
}

export interface FareAvailQualifiers {
  accompaniedByInfantInd: string;
  passengerType: string;
  quantity: string;
  travellerID: string;
  givenName: string;
  surname: string;
  loyalty: FareAvailQualifiersLoyalty;
}

export interface SeatMap {
  message: string;
  changeOfGaugeInd: boolean;
  flight: Flight;
  cabin: Cabin;
  fareAvailQualifiers: FareAvailQualifiers;
  preReservedSeats: PassengerSeatPrice[];
}

export interface PassengerSeatPrice {
  passenger: string;
  seatSelected: string;
  price: string;
  currency: string;
  differentCurrencies: boolean;
}

export interface IReservationSeatSeatPriceTaxes {
  taxCode: string;
  currencyCode: string;
  totalAmount: number;
}

export interface IReservationSeatSeatPrice {
  totalAmount: {
    price: CabinRowSeatOfferPrice;
    taxes: IReservationSeatSeatPriceTaxes[];
  };
}

export interface ReservationSeat {
  boardingPass: boolean;
  nameSelect: {
    nameNumber: string;
  };
  seatSelect: {
    number: string;
  };
  segmentSelect: {
    number: string;
  };
  seatPrice: IReservationSeatSeatPrice;
}

export interface ReserveSeatRequest {
  pnr: string;
  seats: ReservationSeat[];
}

export interface ICancelSeatResponse {
  seatNumber: string;
  segmentNumber: string;
  messageResponse: string;
}

export interface ICancelSeatResponseBody {
  lSeats: ICancelSeatResponse[];
}

export interface ICancelSeatRequestServiceSeats {
  seatNumber: string;
  segmentNumber: string;
}

export interface ICancelSeatRequestService {
  pnr: string;
  all: string;
  seats: ICancelSeatRequestServiceSeats[];
}

export interface ReserveSeatRequestService {
  gds: string;
  cityOfficeId: string;
  body: ReserveSeatRequest;
}

export interface MarketingAirline {
  code: string;
  flightNumber: string;
}

export interface FlightSegment {
  departureDate: string;
  flightNumber: string;
  resBookDesigCode: string;
  destinationLocation: {
    locationCode: string;
  };
  marketingAirline: MarketingAirline;
  originLocation: {
    locationCode: string;
  };
}

export interface ReserveSeatDetailConfirmation {
  flightSegment: FlightSegment;
  number: string;
}

export interface ReserveSeatConfirmation {
  lSeats: [ReserveSeatDetailConfirmation];
}

export interface ReservationSeatPrices {
  reservationSeatPrices: CabinRowSeatOfferPrice[];
}

export interface SeatPosition {
  number: string;
  letter: string;
}

export enum SeatAvailableStatus {
  ADVANCEDBOARDINGPASSSEAT = 'AdvancedBoardingPassSeat',
  ADVANCEDSEATSELECTIONSEAT = 'AdvancedSeatSelectionSeat',
  CHECKINRESERVEDSEAT = 'CheckInReservedSeat',
  DOWNLINEPROTECTEDSEATS = 'DownlineProtectedSeats',
  EXTRASEAT = 'ExtraSeat',
  NOSEATHERE = 'NoSeatHere',
  PROTECTEDSEAT = 'ProtectedSeat',
  RESERVEDSEATGENERIC = 'ReservedSeatGeneric',
  SEATBLOCKEDFORAIRPORTUSE = 'SeatBlockedForAirportUse',
  SEATBLOCKEDFOROTHERREASON = 'SeatBlockedForOtherReason',
  SEATBLOCKEDFORTHROUGHPASSENGERBOARDED = 'SeatBlockedForThroughPassengerBoarded',
  SEATBLOCKEDFORWITHDEADLOAD = 'SeatBlockedForWithDeadload',
  SEATFORGROUPPREALLOCATION = 'SeatForGroupPreAllocation',
  SEATISFREE = 'SeatIsFree',
  SEATISNOTAVAILABLEFORINTERLINETHROUGHCHECKIN = 'SeatIsNotAvailableForInterlineThroughCheckIn',
  SEATISNOTAVAILABLEFORPARTNERAIRLINESUSE = 'SeatIsNotAvailableForPartnerAirlinesUse',
  SEATISOCCUPIED = 'SeatIsOccupied',
  SEATOCCUPIEDFORMEDICALREASONS = 'SeatOccupiedForMedicalReasons',
  SEATPROTECTEDFORCODESHARING = 'SeatProtectedForCodeSharing',
  TRANSITPASSENGERSEATOCCUPIEDBYATRANSITPASSENGERORLOAD = 'TransitPassengerSeatOccupiedByATransitPassengerOrLoad',
  UPLINEPROTECTEDSEAT = 'UplineProtectedSeat',
  OTHER = 'Other'
}
